import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCart } from "./CartContext";
import config from "../config";
import "../styles/success.css";

import { FaHeart, FaRegCircleCheck } from "react-icons/fa6";
import { LuPackage } from "react-icons/lu";
import { FiCalendar } from "react-icons/fi";
import { FaRegEnvelope } from "react-icons/fa6";
import { FaRegHeart } from "react-icons/fa";

const host = config.backendUrl;

const SuccessPage = ({title}) => {

    /* checkout session ID is in the URL */ 
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");

    /* fetching order details from DB using session ID */
    const [orderDetails, setOrderDetails] = useState(null);

    /* cart must be cleared after checkout */
    const { clearCart } = useCart();

    /* on load, clear the cart and fetch order details */
    useEffect(() => {
        document.title = title;
        if (sessionId) {
            fetch(`${host}/api/order-status?session_id=${sessionId}`, {
                method: "GET",
                headers: {
                    "X-API-Key": process.env.REACT_APP_GANDF_KEY,
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    setOrderDetails(data);
                    clearCart();
                })
                .catch((err) => console.error("Error fetching order:", err));
        }
    }, [sessionId]);

    /* convert cents to dollars and round */
    const formatPrice = (price) => {
        price = price / 100;
        const options = {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        };
        return new Intl.NumberFormat("en-US", options).format(price);
    }

    /* convert timestamp to readable date */
    const formatDate = (date) => {
        if (typeof date === "number") {
            date = date;
        } else if (typeof date === "string") {
            date = new Date(date).getTime();
        }
        const options = {
            year: "numeric",
            month: "long",
            day: "numeric",
        };
        return new Intl.DateTimeFormat("en-US", options).format(new Date(date));
    }

    return (
        <div className="success">
            {!(orderDetails && orderDetails.promotion_code) ? (
                <div className="success-header">
                    <FaRegCircleCheck className="success-icon" />
                    <span className="success-title">Thank You for Your Order!</span>
                    <span className="success-subtitle">Your order has been successfully placed and is being processed.</span>
                </div>
            ) : (
                <div className="success-header">
                    <FaRegCircleCheck className="success-icon" />
                    <span className="success-title">Thank You for Your Gift Order!</span>
                    <span className="success-subtitle">Your gift has been successfully sent to the recipient.</span>
                </div>
            )}

            {orderDetails ? (
                <div className="order-details">
                    <div className="order-summary">
                        <span className="order-summary-title">Order Summary</span>

                        <div className="order-summary-items">
                            <div className="order-summary-item">
                                <span className="order-summary-item-label">Order ID</span>
                                <span className="order-summary-item-value">{orderDetails.id}</span>
                            </div>
                            <div className="order-summary-item">
                                <span className="order-summary-item-label">Order Date</span>
                                <span className="order-summary-item-value">{formatDate(orderDetails.created * 1000)}</span>
                            </div>
                            <div className="order-summary-item">
                                <span className="order-summary-item-label">Payment Method</span>
                                <span className="order-summary-item-value">
                                    {orderDetails.payment_method_types[0].charAt(0).toUpperCase() + orderDetails.payment_method_types[0].slice(1)}
                                </span>
                            </div>
                            <div className="order-summary-item">
                                <span className="order-summary-item-label">Email</span>
                                <span className="order-summary-item-value">{orderDetails.email}</span>
                            </div>
                        </div>

                        <span className="order-items-title">Items</span>
                        {orderDetails.line_items.map((item) => (
                            <div key={item.id} className="order-item">
                                <img src={item.image} alt={item.description} className={`${orderDetails.recipient_email ? "order-item-gift-image" : "order-item-image"}`} />
                                <div className="order-item-details">
                                    <span className="order-item-title">{item.description}</span>
                                    { item.promotion_code ? (
                                        <span className="order-item-recipient">
                                            Sent to {orderDetails.recipient_email}
                                        </span>
                                        ) : (
                                        <span className="order-item-recipient">{item.recipient}</span>
                                    )}
                                    <span className="order-item-quantity">Quantity: {item.quantity}</span>
                                    <span className="order-item-value">${(item.amount_subtotal / 100).toFixed(2)}</span>
                                </div>
                            </div>
                        ))}

                        <hr className="order-summary-divider" />

                        <div className="order-total">
                            <div className="order-total-text">
                                <p>Subtotal</p>
                                <p>{formatPrice(orderDetails.amount_subtotal)}</p>
                            </div>


                            {(orderDetails.amount_shipping != null && orderDetails.amount_tax != null) && (
                                <div className="order-total-text">
                                    <p>Shipping</p>
                                    <p>{formatPrice(orderDetails.amount_shipping)}</p>
                                </div>
                            )}

                            {(orderDetails.amount_shipping != null && orderDetails.amount_tax != null) && (
                                <div className="order-total-text">
                                    <p>Tax</p>
                                    <p>{formatPrice(orderDetails.amount_tax)}</p>
                                </div>
                            )}

                            {orderDetails.amount_discount > 0 && (
                                <div className="order-total-text">
                                    <p>Discount</p>
                                    <p>-{formatPrice(orderDetails.amount_discount)}</p>
                                </div>
                            )}

                            <hr className="order-summary-divider" />

                            <div className="order-total-text">
                                <p><strong>Total</strong></p>
                                <p><strong>{formatPrice(orderDetails.amount_total)}</strong></p>
                            </div>
                        </div> 
                    </div>

                    <div className="order-side">

                        {!orderDetails.recipient_email && (
                            <div className="shipping-info">
                                <div className="shipping-info-title">
                                    <LuPackage className="shipping-info-icon" />
                                    <span className="shipping-info-title-text">Information</span>
                                </div>
                                <div className="shipping-info-items">
                                    <span className="shipping-info-value">{orderDetails.shipping.name}</span>
                                    <span className="shipping-info-value">{orderDetails.shipping.address.line1}</span>
                                    {orderDetails.shipping.address.line2 && <span className="shipping-info-value">{orderDetails.shipping.address.line2}</span>}
                                    <span className="shipping-info-value">{orderDetails.shipping.address.city}, {orderDetails.shipping.address.state} {orderDetails.shipping.address.postal_code}</span>
                                    <span className="shipping-info-value">{orderDetails.shipping.address.country}</span>
                                </div>

                                <hr className="order-summary-divider" />

                                <div className="estimated-delivery">
                                    <span className="estimated-delivery-title">Estimated Delivery</span>
                                        <div className="estimated-delivery-date">
                                            <FiCalendar className="estimated-delivery-icon" />
                                            {formatDate(orderDetails.estimated_delivery)}
                                        </div>
                                </div>
                            </div>
                        )}

                        {orderDetails.recipient_email ? (
                            <div className="whats-next-redemption">
                                <span className="whats-next-title">How to Redeem Your Gift</span>
                                <div className="whats-next-text">
                                    <div className="whats-next-text-item">
                                        <FaRegEnvelope className="whats-next-text-icon" />
                                        <span className="whats-next-text-description">We’ve emailed your recipient a gift code they can use at checkout.</span>
                                    </div>
                                    <div className="whats-next-text-item">
                                        <LuPackage className="whats-next-text-icon" />
                                        <span className="whats-next-text-description">Your gift recipient can now personalize their book and redeem your gift code whenever it feels right.</span>
                                    </div>
                                    <div className="whats-next-text-item">
                                        <FaRegHeart className="whats-next-text-icon" />
                                        <span className="whats-next-text-description">If you have any issues with your order, don’t hesitate to reach out to us!</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                        <div className="whats-next">
                            <span className="whats-next-title">What's Next?</span>
                            <div className="whats-next-text">
                                <div className="whats-next-text-item">
                                    <FaRegEnvelope className="whats-next-text-icon" />
                                    <span className="whats-next-text-description">You will receive an email confirmation shortly.</span>
                                </div>
                                <div className="whats-next-text-item">
                                    <LuPackage className="whats-next-text-icon" />
                                    <span className="whats-next-text-description">We'll notify you when your order ships.</span>
                                </div>
                                <div className="whats-next-text-item">
                                    <FaRegHeart className="whats-next-text-icon" />
                                    <span className="whats-next-text-description">If you have any issues with your order, don't hesitate to reach out to us.</span>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            ) : (
                <p>Loading order details...</p>
            )}
        </div>
    );
};

export default SuccessPage;
